/** Reference Data (Demo Mode only) - groups supporting procurement configuration (Procurement
 * Categories, Units of Measure) that should not be primary top-level nav items per the Stage 5
 * spec ("do not expose as a primary procurement readiness journey"). Reuses the existing real
 * CategoriesTab/UomTypesTab components and their typed API clients directly - this page is a
 * navigation regroup only, no mock data. */

import { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, SvgIcon, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import CategoriesTab from '../Categories/components/CategoriesTab';
import UomTypesTab from '../../UomTypes/components/UomTypesTab';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

type ReferenceDataTab = 'categories' | 'uom';

const ReferenceDataPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('procurementOrdering');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultFiltersLoading } = useInstanceMarket();

  const [activeTab, setActiveTab] = useState<ReferenceDataTab>('categories');
  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');

  const canCreateCategory = hasPermissionTo(['CategoryCreate', 'ConfigurationAdmin']);
  const canCreateUomType = hasPermissionTo(['UomTypeCreate', 'ConfigurationAdmin']);

  useEffect(() => {
    if (!defaultFiltersLoading && defaultInstanceId && !currentFilterInstance) {
      setCurrentFilterInstance(defaultInstanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFiltersLoading, defaultInstanceId]);

  const handleFilterChange = (instance: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    if (searchTerm !== undefined) setCurrentFilterSearchTerm(searchTerm);
  };

  const handleCreateNew = () => {
    if (activeTab === 'categories') {
      navigate('/procurement/categories/create');
    } else {
      navigate('/uom-types/create');
    }
  };

  const canCreate = activeTab === 'categories' ? canCreateCategory : canCreateUomType;

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 2 }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('referenceData.pageSubtitle', 'Supporting configuration for classification, reporting and ordering - not a primary procurement readiness journey')}>
            {t('referenceData.pageTitle', 'Reference Data')}
          </PageTitle>
        </Box>

        {canCreate && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
          >
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
            >
              {activeTab === 'categories'
                ? t('referenceData.actionCreateCategory', 'Create New Procurement Category')
                : t('referenceData.actionCreateUom', 'Create New Unit of Measure')}
            </Button>
          </Box>
        )}
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={t('referenceData.tabCategories', 'Procurement Categories')}
          value="categories" />
        <Tab label={t('referenceData.tabUom', 'Units of Measure')}
          value="uom" />
      </Tabs>

      {activeTab === 'categories' && (
        <CategoriesTab
          onCreateNew={handleCreateNew}
          onFilterChange={handleFilterChange}
          initialInstance={currentFilterInstance}
          initialSearchTerm={currentFilterSearchTerm}
        />
      )}
      {activeTab === 'uom' && (
        <UomTypesTab
          onCreateNew={handleCreateNew}
          onFilterChange={handleFilterChange}
          initialInstance={currentFilterInstance}
          initialSearchTerm={currentFilterSearchTerm}
        />
      )}
    </PageArea>
  );
};

export default ReferenceDataPage;
